import React, { Component } from 'react';
import { withRouter } from '../../../../common/utils/NavigationWrapper';

import { Edit as EditIcon, ScatterPlot as FleetIcon } from '@mui/icons-material';

import { Button } from '@mui/material';

import Tile from '../../../../common/components/Tile/Tile';
import FleetEditor from './FleetEditor';
import {
  saveFleet,
  getFleets,
  updateFleet,
  getInstrumentsListForFleet,
  getInstrumentsListForQuery,
} from './FleetEditor.actions';
import Validators from '../../../../common/utils/Validators';
import Store from '../../../../redux/Store';
import { showSnackbar } from '../../../../common/state/snackbar/Snackbar.actions';
import Constants from '../../../../common/constants/Constants';
import Loading from '../../../../common/components/Loading/Loading';
import InstrumentsList from '../../../Dashboard/components/InstrumentsList/InstrumentsList';
import './FleetEditor.css';
import { customFormatQuery } from '../QueryEditor/QueryEditor';
import { defaultOperators } from 'react-querybuilder';

class FleetEditorContainer extends Component {
  constructor() {
    super();

    this.state = {
      isFetchingPage: true,
      isFetchingInstruments: true,
      fleet: { Name: '', Query: '' },
      fleets: [],
      fleetNameErrorText: '',
      fleetQueryErrorText: '',
      instrumentsList: { Data: [], Display: {} },
      filters: [],
    };
  }

  componentDidMount() {
    this._refresh().catch((err) => {
      this.setState({ isFetchingPage: true });
      this._showFailMessage('Error occured');
    });
  }

  render() {
    return (
      <Loading condition={this.state.isFetchingPage}>
        <Tile
          title="Fleet editor"
          TitleIcon={EditIcon}
          helpTag="fleet_editor"
          className="u-mb-full"
          RightContent={
            <Button
              variant="contained"
              data-qa="qa-updateFleet"
              className="u-ml-full"
              color="primary"
              onClick={() => this._saveAndUpdate()}
              disabled={this.state.fleetNameErrorText || this.state.fleetQueryErrorText}
            >
              Update
            </Button>
          }
        >
          <FleetEditor
            fleet={this.state.fleet}
            fleetNameErrorText={this.state.fleetNameErrorText}
            fleetQueryErrorText={this.state.fleetQueryErrorText}
            onChangeCallback={this._onChangeFleet}
            filters={this.state.filters}
          />
          <Button
            variant="contained"
            data-qa="qa-previewFleet"
            className="u-ml-full u-mt-full"
            onClick={() => {
              this._preview();
            }}
            disabled={this.state.fleetQueryErrorText}
          >
            Preview
          </Button>
        </Tile>
        <Loading condition={this.state.isFetchingInstruments}>
          <InstrumentsList
            data={this.state.instrumentsList.Data}
            display={this.state.instrumentsList.Display}
          />
        </Loading>
      </Loading>
    );
  }

  _onChangeFleet = (propertyName, value) => {
    let errors = {};
    switch (propertyName) {
      case 'Name':
        errors.fleetNameErrorText = this._getFleetNameValidationErrorMessage(value);
        break;
      case 'Query':
        break;
    }

    const changedFleet = { ...this.state.fleet };
    changedFleet[propertyName] = value;

    this.setState((prevState) => ({
      fleet: changedFleet,
      ...errors,
    }));
  };

  _refresh = () => {
    return getFleets()
      .then((res) => {
        const fleets = res.body || [];
        const fleet = { ...fleets.find((f) => f.FleetId == this.props.params.id) };

        this.setState({
          fleets,
          fleet,
          isFetchingPage: false,
          isFetchingInstruments: true,
        });

        return getInstrumentsListForFleet(fleet.FleetId);
      })
      .then((res) => {
        let r = res.body;
        if (!r.Data || !r.Display) {
          return;
        }

        //rename the instrument lists's title:
        let tableConfig = r.Display.find((e) => e.Type === 'Table');

        if (tableConfig) {
          tableConfig.Name = 'Fleet composition';
        }

        let tableColumns = tableConfig.Configuration.columnsConfig.map((e) => e.key);

        let columnOptions = r.Filters[0].options.filter(
          (o) => tableColumns.includes(o.value) || tableColumns.includes(`${o.value}.Level`)
        );
        columnOptions = this._addQueryBuilderProps(columnOptions);

        r.Filters[0].options = columnOptions;

        this.setState({ instrumentsList: r, isFetchingInstruments: false, filters: r.Filters });
      })
      .then(() => {
        this._preview();
      });
  };

  _addQueryBuilderProps = (queryBuilderConfig) => {
    return queryBuilderConfig.map((opt) => ({
      name: opt.label,
      label: opt.label,
      datatype: opt.type,
      value: opt.value,
      values: opt.options,
      defaultOperator: 'not_null',
    }));
  };

  _saveAndUpdate = () => {
    this.setState({ isFetchingPage: true });
    let fleetData = this.state.fleet;
    fleetData.Query = customFormatQuery(this.state.fleet.Query);
    saveFleet(fleetData)
      .then(() => updateFleet(this.state.fleet.FleetId))
      .then(() => this._refresh())
      .then((res) => {
        this._showSuccesMessage('Fleet successfully saved and instrument list updated');
      })
      .catch((err) => {
        this.setState({ isFetchingPage: false }, () =>
          this._showFailMessage(this._getFailMessage(err))
        );
      });
  };

  _preview = () => {
    this.setState({ isFetchingInstruments: true });
    let formattedQuery = customFormatQuery(this.state.fleet.Query);
    getInstrumentsListForQuery(formattedQuery)
      .then((res) => {
        let r = res.body;
        if (!r.Data || !r.Display) {
          return;
        }

        //rename the instrument lists's title:
        let tableConfig = r.Display.find((e) => e.Type === 'Table');
        if (tableConfig) {
          tableConfig.Name = 'Fleet composition (preview)';
        }

        this.setState({ instrumentsList: r, isFetchingInstruments: false });
      })
      .catch((err) => {
        this.setState({ isFetchingInstruments: false }, () =>
          this._showFailMessage(this._getFailMessage(err))
        );
      });
  };

  _getFailMessage = (err) => {
    return err && err.response && err.response.body && err.response.body.Message == 'InvalidQuery'
      ? 'Provided query is invalid: ' + err.response.body.ExceptionMessage
      : 'Failed to save and update fleet';
  };

  _getFleetNameValidationErrorMessage = (name) => {
    let validationMessage = '';
    if (!Validators.validateStringBetweenRange(name, 1, 200)) {
      validationMessage = 'Enter an alphanumeric value between 1 and 200 characters';
    }

    for (let fleet of this.state.fleets) {
      if (
        fleet.FleetId != this.state.fleet.FleetId &&
        name.toLowerCase().replace(/\s/g, '') === fleet.Name.toLowerCase().replace(/\s/g, '')
      ) {
        validationMessage = 'A fleet with this name already exists';
      }
    }

    return validationMessage;
  };

  _showSuccesMessage(theMessage) {
    Store.dispatch(
      showSnackbar({
        type: Constants.SNACKBAR_MESSAGE_TYPES.SUCCESS,
        message: theMessage,
      })
    );
  }

  _showFailMessage(theMessage) {
    Store.dispatch(
      showSnackbar({
        type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
        message: theMessage,
      })
    );
  }
}

export default withRouter(FleetEditorContainer);
